import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import "./testing_tool.css";
import "./testing_tool_results.css";
import { getTestingToolBySlug, reAuditTestingToolReport } from "./testing_tool_api_service";
import { parseTestingToolReportResponse } from "./testing_tool_report_utils";
import { LoadingIcon } from "../../../loader/loadingIconNew";
import TestingToolResultsView from "./testing_tool_results_view";

export default function TestingToolResults() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [auditProps, setAuditProps] = useState(null);
  const [isReauditing, setIsReauditing] = useState(false);

  useEffect(() => {
    if (!slug) {
      setLoadError("Invalid audit link.");
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    async function loadAudit() {
      setIsLoading(true);
      setLoadError("");

      try {
        const data = await getTestingToolBySlug(slug);
        if (cancelled) return;

        const parsed = parseTestingToolReportResponse(data);
        const hasResults = Boolean(parsed.report) || parsed.retrievedIssues.length > 0;

        if (!hasResults) {
          setLoadError("Audit not found.");
          setAuditProps(null);
          return;
        }

        setAuditProps({
          report: parsed.report,
          message: parsed.usedMock
            ? (parsed.message ?? "Using sample report (no API key).")
            : "Live response via Claude",
          siteType: parsed.siteType || "other",
          metadata: parsed.metadata,
          retrievedIssues: parsed.retrievedIssues,
          totalOpenFindings: parsed.totalOpenFindings,
          totalResolvedFindings: 0,
          totalEstimatedFixHours: parsed.totalEstimatedFixHours ?? 0,
          testTestingToolId: parsed.testTestingToolId,
          onBackToProjects: () => navigate("/testing-tool"),
        });
      } catch (error) {
        if (cancelled) return;
        setLoadError(error?.message || "Unable to load this audit.");
        setAuditProps(null);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    loadAudit();

    return () => {
      cancelled = true;
    };
  }, [slug, navigate]);

  async function handleReaudit() {
    if (isReauditing) return;

    try {
      setIsReauditing(true);

      const data = await reAuditTestingToolReport(slug);
      const parsed = parseTestingToolReportResponse(data);

      setAuditProps({
        report: parsed.report,
        message: parsed.usedMock
          ? (parsed.message ?? "Using sample report (no API key).")
          : "Live response via Claude",
        siteType: parsed.siteType || "other",
        metadata: parsed.metadata,
        retrievedIssues: parsed.retrievedIssues,
        totalOpenFindings: parsed.totalOpenFindings,
        totalResolvedFindings: 0,
        totalEstimatedFixHours: parsed.totalEstimatedFixHours ?? 0,
        testTestingToolId: parsed.testTestingToolId,
        onBackToProjects: () => navigate("/testing-tool"),
      });
    } catch (error) {
      console.error("Failed to re-audit", error);
    } finally {
      setIsReauditing(false);
    }
  }

  if (isLoading && !auditProps) {
    return (
      <div className="testing_tool">
        <main className="testing-tool-results-body">
          <div className="page-loader accountloading-box result-sections-loader">
            <LoadingIcon />
          </div>
        </main>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="testing_tool">
        <header className="testing_tool-header">
          <div className="testing_tool-header-inner testing_tool-header-inner--results">
            <Link to="/testing-tool" className="testing_tool-logo">
              <img src="/img/testing-tool/UserQ-logo.png" alt="UserQ" />
            </Link>
          </div>
        </header>
        <main className="testing-tool-results-body">
          <div className="testing_tool-audit-error">
            <p>{loadError}</p>
            <Link to="/testing-tool" className="testing-tool-results-btn">
              Start a new audit
            </Link>
          </div>
        </main>
      </div>
    );
  }

  if (!auditProps) {
    return null;
  }

  return (
    <TestingToolResultsView
      {...auditProps}
      onReaudit={handleReaudit}
      isReauditing={isReauditing}
    />
  );
}
